import {
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  defined,
} from "@cesium/engine";
import Input3DData from "../basic/Input3DData";
import Utils from "../basic/Utils";

/**
 * 3D 모델 위치 지정을 위한 사용자 입력(마우스/키보드) 클래스.<br>
 * User input class for specifying 3D model position using mouse/keyboard input.
 *
 * @alias Interaction3DPosition
 * @constructor
 *
 * @param {Input3DData} model 3D 데이터 입력 기능 클래스.<br> Function class for 3D data entry.
 */
class Interaction3DPosition {
  constructor(input3DData) {
    this._handler = undefined;
    this._position = undefined;
    if (input3DData instanceof Input3DData) {
      this._model = input3DData;
    }
    this._infoLabelEntity = undefined;
    this._mousePosition = undefined;
    this._isOn = false;
    this._keyDownEvent = undefined;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /**
   * 3D 데이터 입력 기능 클래스.<br>
   * Function class for 3D data entry.
   * @type {Object}
   */
  set model(model) {
    if (model instanceof Input3DData || model === undefined) {
      this._model = model;
    } else {
      console.error("Type of parameter doesn't match");
    }
  }
  get model() {
    return this._model;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }
  get mousePosition() {
    return this._mousePosition;
  }

  /**
   * 핸들러 비/활성화.</br>
   * Handler enabled or disabled.
   * @type {boolean}
   * @readonly
   */
  set isOn(boolean) {
    this._isOn = boolean;
  }
  get isOn() {
    return this._isOn;
  }

  /*
   * 키 다운 시 실행 이벤트.</br>
   * Event to execute when a key is pressed.
   * @type {Function}
   */
  set keyDownEvent(keyDownEvent) {
    this._keyDownEvent = keyDownEvent;
  }
  get keyDownEvent() {
    return this._keyDownEvent;
  }

  /**
   * 3D 모델 위치 지정 핸들러 실행. (input3DData 초기화 필수.)</br>
   * Executing the handler for specifying the position of the 3D model. (input3DData initialization is required.)
   */
  on() {
    if (!this.isOn) {
      this.isOn = true;
      this.handler = new ScreenSpaceEventHandler(this.model.viewer.canvas);

      const label = Utils.createInfoLabel(
        this.model.viewer.container,
        "데이터를 추가 할 위치에 왼 클릭해주세요"
      );
      this.infoLabelEntity = label;

      this.createLeftClickHandler();
      this.createMouseMoveHandler();
      this.createRightClickHandler();
      this.createKeyDownHandler();
      // this.createWindowMoveEvent();
    }
  }

  /**
   * 3D 모델 위치 지정 핸들러 종료. (input3DData 초기화 필수.)</br>
   * End 3D model positioning handler. (input3DData initialization is required.)
   */
  off() {
    if (this.isOn) {
      this.isOn = false;

      if (this.handler) {
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
      }
      window.removeEventListener("keydown", this.keyDownEvent);
      window.removeEventListener("mousemove", this.mousemoveEvent);

      this.infoLabelEntity.remove();

      this.handler = undefined;
      this.infoLabelEntity = undefined;
      this.mousePosition = undefined;
      this.keyDownEvent = undefined;
      this.mousemoveEvent = undefined;
    }
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      const position = this.mousePosition;
      if (defined(position)) {
        this.model.position = position;

        // 모델 생성
        this.model.createModel(position);

        this.off();
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const handler = this.handler;
    const viewer = this.model.viewer;

    handler.setInputAction((e) => {
      const position = Utils.pickPosition(e.endPosition, viewer);

      if (defined(position)) {
        this.mousePosition = position;

        // 도움말 라벨 좌표 이동
        Utils.changeLabelLocation(
          viewer,
          this.mousePosition,
          this.infoLabelEntity
        );
      } else {
        this.infoLabelEntity.classList.add("hide");
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;

    handler.setInputAction(() => {
      this.off();
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 키 다운 이벤트 핸들러 생성.</br>
   * Creates a key down event handler.
   */
  createKeyDownHandler() {
    window.addEventListener(
      "keydown",
      (this.keyDownEvent = (event) => {
        this.onKeyDown(event);
      })
    );
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    if (event.key === "Escape") {
      // 동적 Entity 제거
      this.off();
    }
  }
}

export default Interaction3DPosition;
